import { useEffect, useRef, useCallback, useState } from "react"
import { useTareas } from "../context/TareasContext"
import { EditorPaginasProvider, useEditorPaginas } from "./EditorPaginasContext"
import EditorSidebar from "./EditorSidebar"
import EditorJS from "@editorjs/editorjs"
import Header from "@editorjs/header"
import List from "@editorjs/list"
import Checklist from "@editorjs/checklist"
import Quote from "@editorjs/quote"
import Code from "@editorjs/code"
import Delimiter from "@editorjs/delimiter"
import InlineCode from "@editorjs/inline-code"
import Marker from "@editorjs/marker"
import Table from "@editorjs/table"
import Embed from "@editorjs/embed"
import { FaSpinner, FaCheck, FaImage, FaFileAlt } from "react-icons/fa"

const SIDEBAR_STORAGE_KEY = "editor-paginas-sidebar-width"
const DEFAULT_SIDEBAR = 240
const MIN_SIDEBAR = 180
const MAX_SIDEBAR = 480
const MIN_EDITOR = 400

const loadSidebarWidth = () => {
  try {
    const raw = localStorage.getItem(SIDEBAR_STORAGE_KEY)
    if (!raw) return DEFAULT_SIDEBAR
    return Math.max(MIN_SIDEBAR, Math.min(MAX_SIDEBAR, Number(raw) || DEFAULT_SIDEBAR))
  } catch {
    return DEFAULT_SIDEBAR
  }
}

const saveSidebarWidth = (w) => {
  try { localStorage.setItem(SIDEBAR_STORAGE_KEY, String(w)) } catch {}
}

const SidebarResizer = ({ onDragStart }) => (
  <div
    role="separator"
    aria-orientation="vertical"
    className="absolute top-0 bottom-0 right-0 translate-x-1/2 w-1 cursor-col-resize z-10 group
      hover:bg-orange-400/50 active:bg-orange-500/70 transition-colors"
    onMouseDown={onDragStart}
    style={{ touchAction: "none" }}
  >
    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-gray-300 group-hover:bg-orange-400" />
  </div>
)

const EDITOR_TOOLS = {
  header: {
    class: Header,
    config: { levels: [1, 2, 3], defaultLevel: 2 },
    shortcut: "CMD+SHIFT+H",
  },
  list: {
    class: List,
    inlineToolbar: true,
    config: { defaultStyle: "unordered" },
    shortcut: "CMD+SHIFT+L",
  },
  checklist: { class: Checklist, inlineToolbar: true },
  quote: {
    class: Quote,
    inlineToolbar: true,
    config: { quotePlaceholder: "Escribe una cita...", captionPlaceholder: "Autor" },
  },
  code: { class: Code },
  delimiter: { class: Delimiter },
  inlineCode: { class: InlineCode, shortcut: "CMD+SHIFT+M" },
  marker: { class: Marker, shortcut: "CMD+SHIFT+1" },
  table: {
    class: Table,
    inlineToolbar: true,
    config: { rows: 2, cols: 3 },
  },
  embed: {
    class: Embed,
    config: { services: { youtube: true, vimeo: true, codepen: true } },
  },
}

const COVER_COLORS = [
  "#FEE2E2","#FEF3C7","#D1FAE5","#DBEAFE","#EDE9FE","#FCE7F3",
  "#F3F4F6","#FECACA","#FDE68A","#A7F3D0","#BFDBFE","#C4B5FD",
]

const EMOJIS = ["📄","📝","📋","📌","💡","⭐","🎯","🔥","📊","🗂️","📁","🏷️","✅","🚀","💼","📖"]

const EMPTY_DATA = { time: Date.now(), blocks: [] }

/* ── Editor panel (right side) ───────────────────────────── */
const EditorPanel = () => {
  const {
    currentPage, loadingPage, saving, saved,
    updatePageTitle, updatePageIcon, updatePageCover, updateData,
  } = useEditorPaginas()

  const editorRef = useRef(null)
  const holderRef = useRef(null)
  const [showIconPicker, setShowIconPicker] = useState(false)
  const [showCoverPicker, setShowCoverPicker] = useState(false)
  const [editorReady, setEditorReady] = useState(false)
  const pageIdRef = useRef(null)

  const destroyEditor = useCallback(() => {
    if (editorRef.current?.destroy) {
      editorRef.current.destroy()
      editorRef.current = null
    }
    setEditorReady(false)
  }, [])

  const handleEditorChange = useCallback(async () => {
    if (!editorRef.current || !pageIdRef.current) return
    try {
      const output = await editorRef.current.save()
      updateData(output)
    } catch (err) {
      console.error("[EditorJS] onChange save error:", err)
    }
  }, [updateData])

  // (Re)init editor when selected page changes
  useEffect(() => {
    if (loadingPage || !currentPage) {
      destroyEditor()
      pageIdRef.current = null
      return
    }

    if (pageIdRef.current === currentPage.id && editorRef.current) return

    destroyEditor()
    pageIdRef.current = currentPage.id
    const timer = setTimeout(() => {
      if (!holderRef.current) return

      editorRef.current = new EditorJS({
        holder: holderRef.current,
        tools: EDITOR_TOOLS,
        data: currentPage.data || EMPTY_DATA,
        placeholder: "Escribe aquí o presiona Tab para ver los bloques disponibles...",
        inlineToolbar: ["bold", "italic", "inlineCode", "marker", "link"],
        autofocus: true,
        onReady: () => setEditorReady(true),
        onChange: () => handleEditorChange(),
      })
    }, 50)

    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage?.id, loadingPage])

  useEffect(() => {
    return () => destroyEditor()
  }, [destroyEditor])

  const handleManualSave = useCallback(async () => {
    if (!editorRef.current) return
    try {
      const output = await editorRef.current.save()
      updateData(output)
    } catch (err) {
      console.error("[EditorJS] Manual save error:", err)
    }
  }, [updateData])

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault()
        handleManualSave()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [handleManualSave])

  if (loadingPage) {
    return (
      <div className="flex items-center justify-center h-full flex-1">
        <FaSpinner className="animate-spin text-xl text-gray-400" />
      </div>
    )
  }

  if (!currentPage) return null

  return (
    <div className="flex-1 min-w-0 flex flex-col h-full">
      {/* Save indicator (minimal) */}

      {/* Editor area */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full">
          <div className="max-w-3xl mx-auto py-3 px-6">

            {/* Cover */}
            {currentPage.coverColor && (
              <div
                className="h-16 rounded-lg mb-2 relative group"
                style={{ backgroundColor: currentPage.coverColor }}
              >
                <button
                  onClick={() => setShowCoverPicker(true)}
                  className="absolute bottom-1 right-1.5 opacity-0 group-hover:opacity-100 text-[10px] px-1.5 py-0.5 bg-white/80 rounded text-gray-600 hover:bg-white transition-all"
                >
                  Cambiar
                </button>
              </div>
            )}

            {/* Controls */}
            <div className="flex items-center gap-2 mb-1 text-gray-400">
              {!currentPage.coverColor && (
                <button
                  onClick={() => setShowCoverPicker(v => !v)}
                  className="flex items-center gap-1 text-[10px] hover:text-gray-600 hover:bg-gray-100 px-1.5 py-0.5 rounded transition-colors"
                >
                  <FaImage className="text-[9px]" /> Portada
                </button>
              )}
            </div>

            {/* Cover picker */}
            {showCoverPicker && (
              <div className="flex flex-wrap gap-1.5 mb-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                {COVER_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => { updatePageCover(c); setShowCoverPicker(false) }}
                    className="w-8 h-5 rounded border-2 transition-all hover:scale-110"
                    style={{
                      backgroundColor: c,
                      borderColor: currentPage.coverColor === c ? "#374151" : "transparent",
                    }}
                  />
                ))}
                {currentPage.coverColor && (
                  <button
                    onClick={() => { updatePageCover(""); setShowCoverPicker(false) }}
                    className="text-[10px] text-red-400 hover:text-red-600 px-1.5"
                  >
                    Quitar
                  </button>
                )}
              </div>
            )}

            {/* Icon + Title + Save indicator */}
            <div className="flex items-center gap-2 mb-3">
              <div className="relative">
                <button
                  onClick={() => setShowIconPicker(v => !v)}
                  className="text-2xl leading-none hover:bg-gray-100 rounded p-0.5 transition-colors"
                  title="Cambiar icono"
                >
                  {currentPage.icon || "📄"}
                </button>
                {showIconPicker && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-2 grid grid-cols-8 gap-1 min-w-[200px]">
                    {EMOJIS.map(e => (
                      <button
                        key={e}
                        onClick={() => { updatePageIcon(e); setShowIconPicker(false) }}
                        className="text-lg p-0.5 rounded hover:bg-gray-100 transition-colors"
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <input
                type="text"
                value={currentPage.title || ""}
                onChange={(e) => updatePageTitle(e.target.value)}
                placeholder="Sin título"
                className="flex-1 text-xl font-bold text-gray-800 outline-none border-none bg-transparent placeholder:text-gray-300"
              />

              <div className="flex-shrink-0 w-20">
                {saving && (
                  <span className="text-[10px] text-gray-400 flex items-center gap-1">
                    <FaSpinner className="animate-spin text-[9px]" /> Guardando
                  </span>
                )}
                {!saving && saved && (
                  <span className="text-[10px] text-green-500 flex items-center gap-1">
                    <FaCheck className="text-[9px]" /> Guardado
                  </span>
                )}
              </div>
            </div>

            {/* Editor.js holder */}
            <div
              ref={holderRef}
              className="editorjs-container min-h-[400px]"
            />
          </div>
        </div>
      </div>

      <style>{`
        .editorjs-container .codex-editor {
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        .editorjs-container .ce-block__content,
        .editorjs-container .ce-toolbar__content {
          max-width: 100%;
        }
        .editorjs-container .ce-paragraph {
          font-size: 14px;
          line-height: 1.7;
          color: #374151;
        }
        .editorjs-container .ce-header { color: #111827; }
        .editorjs-container h1.ce-header { font-size: 28px; font-weight: 700; }
        .editorjs-container h2.ce-header { font-size: 22px; font-weight: 600; }
        .editorjs-container h3.ce-header { font-size: 17px; font-weight: 600; }
        .editorjs-container .cdx-quote {
          border-left: 3px solid #f97316;
          padding-left: 16px;
        }
        .editorjs-container .cdx-quote__text {
          font-size: 14px; color: #4B5563; font-style: italic;
        }
        .editorjs-container .cdx-checklist__item-checkbox { border-radius: 4px; }
        .editorjs-container .cdx-checklist__item--checked .cdx-checklist__item-checkbox {
          background-color: #f97316; border-color: #f97316;
        }
        .editorjs-container .ce-code__textarea {
          font-family: 'JetBrains Mono', 'Fira Code', monospace;
          font-size: 13px; border-radius: 10px;
          background: #1F2937; color: #E5E7EB;
          padding: 16px; border: none;
        }
        .editorjs-container .cdx-marker {
          background: rgba(249, 115, 22, 0.2);
          padding: 2px 4px; border-radius: 3px;
        }
        .editorjs-container .tc-table { border-radius: 8px; overflow: hidden; }
        .editorjs-container .tc-row--heading {
          background: #F9FAFB; font-weight: 600; font-size: 12px; color: #6B7280;
        }
        .editorjs-container .tc-cell { font-size: 13px; border-color: #F3F4F6; }
        .editorjs-container .ce-delimiter::before { color: #D1D5DB; }
        .editorjs-container .cdx-input.cdx-quote__caption { font-size: 12px; color: #9CA3AF; }
        .editorjs-container .ce-inline-toolbar {
          border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.12);
        }
        .editorjs-container .ce-toolbar__plus,
        .editorjs-container .ce-toolbar__settings-btn {
          color: #9CA3AF; border-radius: 6px;
        }
        .editorjs-container .ce-toolbar__plus:hover,
        .editorjs-container .ce-toolbar__settings-btn:hover {
          background-color: #FFF7ED; color: #F97316;
        }
        .editorjs-container .ce-popover {
          border-radius: 12px; box-shadow: 0 8px 30px rgba(0,0,0,0.12);
        }
      `}</style>
    </div>
  )
}

/* ── Inner layout (sidebar + editor) ─────────────────────── */
const EditorJSInner = () => {
  const { currentPage, loadingPage } = useEditorPaginas()
  const containerRef = useRef(null)
  const [sidebarWidth, setSidebarWidth] = useState(loadSidebarWidth)
  const [resizing, setResizing] = useState(false)

  useEffect(() => {
    if (!resizing) return

    const handleMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      const x = e.clientX - rect.left
      const maxW = Math.min(MAX_SIDEBAR, rect.width - MIN_EDITOR)
      setSidebarWidth(Math.max(MIN_SIDEBAR, Math.min(maxW, x)))
    }

    const handleEnd = () => {
      setSidebarWidth((w) => { saveSidebarWidth(w); return w })
      setResizing(false)
    }

    window.addEventListener("mousemove", handleMove)
    window.addEventListener("mouseup", handleEnd)
    return () => {
      window.removeEventListener("mousemove", handleMove)
      window.removeEventListener("mouseup", handleEnd)
    }
  }, [resizing])

  const startResize = (e) => {
    e.preventDefault()
    setResizing(true)
  }

  return (
    <div
      ref={containerRef}
      className={`flex h-full ${resizing ? "cursor-col-resize select-none" : ""}`}
    >
      <div
        className="relative flex-shrink-0"
        style={{ width: sidebarWidth, minWidth: MIN_SIDEBAR }}
      >
        <EditorSidebar />
        <SidebarResizer onDragStart={startResize} />
      </div>
      <div className="flex-1 min-w-0" style={{ minWidth: MIN_EDITOR }}>
        {!currentPage && !loadingPage ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3 text-gray-400">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                <FaFileAlt className="text-2xl text-gray-300" />
              </div>
              <p className="text-sm font-medium text-gray-500">Selecciona o crea una página</p>
              <p className="text-xs text-gray-400">Usa el panel izquierdo para navegar</p>
            </div>
          </div>
        ) : (
          <EditorPanel />
        )}
      </div>
    </div>
  )
}

/* ── Main wrapper ────────────────────────────────────────── */
const EditorJSPage = () => {
  const { selectedProject } = useTareas()

  return (
    <EditorPaginasProvider projectId={selectedProject?.id}>
      <EditorJSInner />
    </EditorPaginasProvider>
  )
}

export default EditorJSPage
